/**
 * SEO primitives: canonical site URL and DOM helpers for per-route metadata.
 *
 * The static defaults live in index.html (title, description, OG tags,
 * canonical). Public routes override them via <PageMeta>, which calls
 * applyPageMeta/restorePageMeta below. Helpers mutate existing tags in place
 * (upsert) so the document never carries duplicate title/meta/canonical tags —
 * this matters for the build-time prerender, which snapshots the live DOM.
 */

export const SITE_URL = "https://stasht.app";

export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;
export const BLOG_OG_IMAGE = `${SITE_URL}/og-image-blog.png`;
export { DECK_PAGE_META } from "./deckMeta.js";

export interface PageMetaValues {
  title: string;
  description: string;
  canonical: string;
  ogType: string;
  ogImage: string;
  ogImageAlt: string;
}

interface MetaBinding {
  attr: "name" | "property";
  key: string;
  pick: (values: PageMetaValues) => string;
}

// Every tag PageMeta manages, mapped to the value it carries.
const META_BINDINGS: MetaBinding[] = [
  { attr: "name", key: "description", pick: v => v.description },
  { attr: "property", key: "og:title", pick: v => v.title },
  { attr: "property", key: "og:description", pick: v => v.description },
  { attr: "property", key: "og:url", pick: v => v.canonical },
  { attr: "property", key: "og:type", pick: v => v.ogType },
  { attr: "property", key: "og:image", pick: v => v.ogImage },
  { attr: "property", key: "og:image:alt", pick: v => v.ogImageAlt },
  { attr: "property", key: "twitter:title", pick: v => v.title },
  { attr: "property", key: "twitter:description", pick: v => v.description },
  { attr: "property", key: "twitter:url", pick: v => v.canonical },
  { attr: "property", key: "twitter:image", pick: v => v.ogImage },
  { attr: "property", key: "twitter:image:alt", pick: v => v.ogImageAlt },
];

const JSONLD_ATTR = "data-page-jsonld";

const upsertMeta = (attr: "name" | "property", key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertCanonical = (href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const readMeta = (attr: "name" | "property", key: string): string =>
  document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)?.getAttribute("content") ?? "";

const readCurrentValues = (): PageMetaValues => ({
  title: document.title,
  description: readMeta("name", "description"),
  canonical: document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.getAttribute("href") ?? SITE_URL,
  ogType: readMeta("property", "og:type"),
  ogImage: readMeta("property", "og:image"),
  ogImageAlt: readMeta("property", "og:image:alt"),
});

// Snapshot of the index.html defaults, captured before the first override so
// SPA navigation back to non-public routes restores the original tags.
let defaultValues: PageMetaValues | null = null;

const writeValues = (values: PageMetaValues) => {
  document.title = values.title;
  META_BINDINGS.forEach(binding => upsertMeta(binding.attr, binding.key, binding.pick(values)));
  upsertCanonical(values.canonical);
};

export const applyPageMeta = (values: PageMetaValues) => {
  defaultValues ??= readCurrentValues();
  writeValues(values);
};

export const restorePageMeta = () => {
  if (defaultValues) writeValues(defaultValues);
};

/** Replace all PageMeta-managed JSON-LD blocks with the given ones. */
export const setPageJsonLd = (blocks: ReadonlyArray<Record<string, unknown>>) => {
  clearPageJsonLd();
  blocks.forEach(block => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute(JSONLD_ATTR, "true");
    script.textContent = JSON.stringify(block);
    document.head.appendChild(script);
  });
};

export const clearPageJsonLd = () => {
  document.head.querySelectorAll(`script[${JSONLD_ATTR}]`).forEach(el => el.remove());
};

/** Absolute canonical URL for a site path ("/faq" → "https://stasht.app/faq"). */
export const canonicalUrl = (path: string): string => {
  const normalized = path === "/" ? "/" : path.replace(/\/+$/, "");
  return `${SITE_URL}${normalized}`;
};
